import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { formatDateOrEmpty } from '@helpers/dates';
import { Typography } from '@components/Typography';
import { useAccountPeople, useTranslation } from '@hooks';
import { ConversationActivity } from '@constants/conversations';
import { INTERNAL_NOTE_COLORS } from './internalNoteColors';
import { FEED_BACKGROUND } from './styles';

const ConversationsEvent = ({ data, agents }) => {
    const { t } = useTranslation();
    const { getPersonName } = useAccountPeople();
    const language = useSelector(state => state.auth.user?.language);

    const agent = useMemo(() => agents.find(agent => agent.id === data.agentId), [agents, data.agentId]);
    const ActivityText = {
        [ConversationActivity.Started]: 'InboxPage.conversationStarted',
        [ConversationActivity.Opened]: 'InboxPage.reopenedBy',
        [ConversationActivity.Closed]: 'InboxPage.resolvedBy',
        [ConversationActivity.Reassigned]: '',
        [ConversationActivity.TeamChanged]: 'InboxPage.teamChangedBy',
    };

    if (data.type === ConversationActivity.Reassigned) {
        return null;
    }

    const isTeamChanged = data.type === ConversationActivity.TeamChanged;
    // A team move without an actor is an automatic correction after a channel
    // team change; the agent fallback is for Pulse activities
    const isAutomaticTeamChange = isTeamChanged && data.userId == null;
    const userName = getPersonName(data.userId, '-');
    const actorName = isTeamChanged ? userName : agent?.name || userName;

    return (
        <>
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', marginTop: 8 }}>
                <Typography
                    variant="caption"
                    sx={{
                        color: 'var(--text-tertiary)',
                        padding: '0 8px',
                        zIndex: 2,
                        backgroundColor: FEED_BACKGROUND,
                        textAlign: 'center',
                    }}
                >
                    {t(isAutomaticTeamChange ? 'InboxPage.teamChangedAuto' : ActivityText[data.type], {
                        date: formatDateOrEmpty(data.createdAt, 'd MMM • HH:mm', language),
                        name: actorName,
                        ...(isTeamChanged && {
                            fromTeam: data.previousTeamName || t('InboxPage.deletedTeam'),
                            toTeam: data.currentTeamName || t('InboxPage.deletedTeam'),
                        }),
                    })}
                </Typography>
                <div
                    style={{
                        borderTop: '1px dashed rgba(0, 0, 0, 0.12)',
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: '50%',
                    }}
                />
            </div>
            {isTeamChanged && data.note && (
                <div
                    style={{
                        margin: '8px auto 0',
                        maxWidth: '78%',
                        padding: '8px 12px',
                        borderRadius: 8,
                        backgroundColor: INTERNAL_NOTE_COLORS.background,
                        border: `1px solid ${INTERNAL_NOTE_COLORS.border}`,
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'block',
                            fontWeight: 700,
                            color: INTERNAL_NOTE_COLORS.label,
                        }}
                    >
                        {t('InboxPage.internalNote').toUpperCase()}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                        {data.note}
                    </Typography>
                </div>
            )}
        </>
    );
};

export default ConversationsEvent;
